import type { ParsedTxAction } from '../lib/txActions'
import { actionDangerLabel, summarizeActionData } from '../lib/txActions'

interface TxConfirmModalProps {
  actions: ParsedTxAction[]
  broadcast: boolean
  dappUrl: string
  accountName: string
  busy?: boolean
  onApprove: () => void
  onReject: () => void
  labels: {
    title: string
    subtitle: string
    broadcastNote: string
    signOnlyNote: string
    approve: string
    reject: string
    noActions: string
  }
}

export default function TxConfirmModal({
  actions,
  broadcast,
  dappUrl,
  accountName,
  busy = false,
  onApprove,
  onReject,
  labels,
}: TxConfirmModalProps) {
  let host = dappUrl
  try {
    host = new URL(dappUrl).hostname
  } catch {
    /* keep raw */
  }

  return (
    <div className="absolute inset-0 z-40 flex items-end sm:items-center justify-center bg-black/85 px-3 pb-safe">
      <div
        className="w-full max-w-md max-h-[85vh] overflow-y-auto rounded-t-2xl sm:rounded-2xl border border-white/10 p-5 shadow-2xl"
        style={{ background: '#0c100c' }}
      >
        <h2 className="text-lg font-bold text-white text-center mb-1">{labels.title}</h2>
        <p className="text-xs text-center mb-4" style={{ color: '#9ca3af' }}>
          {labels.subtitle}
        </p>

        <div
          className="rounded-xl px-3 py-2 mb-4 text-xs"
          style={{ background: '#141914', border: '1px solid rgba(255,255,255,0.06)' }}
        >
          <p style={{ color: '#6b7280' }}>dApp</p>
          <p className="text-white font-medium truncate">{host}</p>
          <p className="mt-1" style={{ color: '#6b7280' }}>
            {accountName}
          </p>
        </div>

        <p className="text-xs mb-2" style={{ color: broadcast ? '#f59e0b' : '#6b7280' }}>
          {broadcast ? labels.broadcastNote : labels.signOnlyNote}
        </p>

        <div className="space-y-2 mb-5">
          {actions.length === 0 ? (
            <p className="text-sm text-center py-4" style={{ color: '#6b7280' }}>
              {labels.noActions}
            </p>
          ) : (
            actions.map((action, i) => {
              const badge = actionDangerLabel(action.danger)
              const hot =
                action.danger === 'transfer' ||
                action.danger === 'updateauth' ||
                action.danger === 'linkauth'
              return (
                <div
                  key={`${action.account}-${action.name}-${i}`}
                  className="rounded-xl px-3 py-2.5"
                  style={{
                    background: hot ? 'rgba(239,68,68,0.08)' : '#141914',
                    border: `1px solid ${hot ? 'rgba(239,68,68,0.35)' : 'rgba(255,255,255,0.06)'}`,
                  }}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-white font-semibold text-sm font-mono">
                      {action.account} → {action.name}
                    </span>
                    {badge && (
                      <span
                        className="text-[10px] font-bold px-1.5 py-0.5 rounded"
                        style={{
                          background: hot ? 'rgba(239,68,68,0.25)' : 'rgba(67,160,71,0.2)',
                          color: hot ? '#fca5a5' : '#86efac',
                        }}
                      >
                        {badge}
                      </span>
                    )}
                  </div>
                  <p className="text-[11px] break-all" style={{ color: '#9ca3af' }}>
                    {summarizeActionData(action.data)}
                  </p>
                </div>
              )
            })
          )}
        </div>

        <div className="flex gap-3">
          <button
            type="button"
            className="btn-secondary flex-1"
            style={{ height: 48 }}
            disabled={busy}
            onClick={onReject}
          >
            {labels.reject}
          </button>
          <button
            type="button"
            className="btn-primary flex-1"
            style={{ height: 48 }}
            disabled={busy}
            onClick={onApprove}
          >
            {labels.approve}
          </button>
        </div>
      </div>
    </div>
  )
}
